import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  startOfDay,
  endOfDay,
} from 'date-fns';
import { calendarApi } from '@/lib/api/calendar.api';
import { useCalendarStore } from '@/store/calendar.store';
import { useOrganizationStore } from '@/store/organization.store';
import type {
  CalendarEventsResponse,
  CalendarView,
  CalendarFilters,
} from '@scrambl/shared';

// Query key factory for calendar queries
export const calendarKeys = {
  all: ['calendar'] as const,
  events: (params: Record<string, unknown>) =>
    [...calendarKeys.all, 'events', params] as const,
  sync: () => [...calendarKeys.all, 'sync'] as const,
};

// Calculate date range based on view type
export const getDateRangeForView = (
  view: CalendarView,
  date: Date
): { start: Date; end: Date } => {
  switch (view) {
    case 'month':
      // Include surrounding weeks for month view (calendar grid)
      return {
        start: startOfWeek(startOfMonth(date), { weekStartsOn: 0 }),
        end: endOfWeek(endOfMonth(date), { weekStartsOn: 0 }),
      };
    case 'week':
      return {
        start: startOfWeek(date, { weekStartsOn: 0 }),
        end: endOfWeek(date, { weekStartsOn: 0 }),
      };
    case 'day':
      return {
        start: startOfDay(date),
        end: endOfDay(date),
      };
    case 'agenda':
      // Agenda view shows next 30 days by default
      return {
        start: startOfDay(date),
        end: addDays(date, 30),
      };
    default:
      return {
        start: startOfWeek(startOfMonth(date), { weekStartsOn: 0 }),
        end: endOfWeek(endOfMonth(date), { weekStartsOn: 0 }),
      };
  }
};

interface UseCalendarEventsOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch calendar events based on current view, date, and filters
 * Automatically updates when store state changes
 */
export function useCalendarEvents(options: UseCalendarEventsOptions = {}) {
  const { enabled = true } = options;

  // Require organization context before making API calls
  const currentOrganization = useOrganizationStore((state) => state.currentOrganization);

  const currentView = useCalendarStore((state) => state.currentView);
  const currentDate = useCalendarStore((state) => state.currentDate);
  const showTasks = useCalendarStore((state) => state.showTasks);
  const showExternalEvents = useCalendarStore(
    (state) => state.showExternalEvents
  );
  const filters = useCalendarStore((state) => state.filters);

  // Calculate date range based on view
  const dateRange = useMemo(
    () => getDateRangeForView(currentView, currentDate),
    [currentView, currentDate]
  );

  // Build query params
  const queryParams = useMemo(
    () => ({
      startDate: dateRange.start.toISOString(),
      endDate: dateRange.end.toISOString(),
      includeTasks: showTasks,
      includeExternalEvents: showExternalEvents,
      projectIds: filters.projectIds.length > 0 ? filters.projectIds : undefined,
      assigneeIds:
        filters.assigneeIds.length > 0 ? filters.assigneeIds : undefined,
      priorities: filters.priorities.length > 0 ? filters.priorities : undefined,
      statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
      tagIds: filters.tagIds.length > 0 ? filters.tagIds : undefined,
      view: currentView,
    }),
    [dateRange, showTasks, showExternalEvents, filters, currentView]
  );

  return useQuery<CalendarEventsResponse>({
    queryKey: calendarKeys.events(queryParams),
    queryFn: () => calendarApi.getEvents(queryParams),
    // Only fetch when organization context is available (prevents 403 on initial load)
    enabled: enabled && !!currentOrganization?.id,
    staleTime: 2 * 60 * 1000, // Consider data fresh for 2 minutes
    gcTime: 5 * 60 * 1000, // Keep in cache for only 5 minutes (short-lived)
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: 'always', // Always refetch on mount to ensure fresh data when navigating
    refetchOnReconnect: false, // Don't refetch on reconnect
  });
}

/**
 * Hook to fetch calendar events for a specific date range
 * Useful for prefetching adjacent time periods
 */
export function useCalendarEventsForRange(
  startDate: Date,
  endDate: Date,
  options: UseCalendarEventsOptions = {}
) {
  const { enabled = true } = options;

  // Require organization context before making API calls
  const currentOrganization = useOrganizationStore((state) => state.currentOrganization);

  const showTasks = useCalendarStore((state) => state.showTasks);
  const showExternalEvents = useCalendarStore(
    (state) => state.showExternalEvents
  );
  const filters = useCalendarStore((state) => state.filters);

  const queryParams = useMemo(
    () => ({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      includeTasks: showTasks,
      includeExternalEvents: showExternalEvents,
      projectIds: filters.projectIds.length > 0 ? filters.projectIds : undefined,
      assigneeIds:
        filters.assigneeIds.length > 0 ? filters.assigneeIds : undefined,
      priorities: filters.priorities.length > 0 ? filters.priorities : undefined,
      statuses: filters.statuses.length > 0 ? filters.statuses : undefined,
      tagIds: filters.tagIds.length > 0 ? filters.tagIds : undefined,
    }),
    [startDate, endDate, showTasks, showExternalEvents, filters]
  );

  return useQuery<CalendarEventsResponse>({
    queryKey: calendarKeys.events(queryParams),
    queryFn: () => calendarApi.getEvents(queryParams),
    // Only fetch when organization context is available
    enabled: enabled && !!currentOrganization?.id,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
}

/**
 * Hook to sync external calendars
 */
export function useSyncExternalCalendars() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => calendarApi.syncExternalCalendars(),
    onSuccess: () => {
      // Invalidate all calendar queries to refetch with fresh data
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
    },
  });
}

/**
 * Hook to invalidate calendar cache
 * Useful after task updates
 */
export function useInvalidateCalendar() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: calendarKeys.all });
  };
}

